import { createRootRoute, Outlet, useRouterState } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import { setPageMeta } from '../lib/seo'
import { NotFound } from '../components/NotFound'
import { CookieConsent } from '../components/CookieConsent'
import { trackPageView } from '../lib/analytics'
import { loadAds, shouldLoadAdsNow } from '../lib/ads'
import { useAuthStore } from '../stores/authStore'
import { useUserStore } from '../stores/userStore'

/**
 * Zentraler Titel/Meta-Mechanismus.
 * useRouterState liefert den aktuellen Pfad bei jedem Routenwechsel reaktiv.
 * setPageMeta setzt document.title + <meta name="description"> anhand der
 * i18n-gestützten Route-Meta-Map (lib/seo.ts).
 * Dynamische Seiten (HotspotDetail, WalletDetail) überschreiben danach via setDynamicMeta.
 *
 * FIN-SWEEP (shell-dashboard): useTranslation() abonniert diese Komponente auf
 * Sprachwechsel (i18n.language ändert sich → Re-Render). Ohne diese Abhängigkeit
 * im useEffect blieb der Titel nach einem Sprachwechsel ohne Navigation auf der
 * zuletzt gesetzten Sprache stehen (z.B. "Monatsbericht · HeliumTracker" auch
 * nach Wechsel auf EN) — global für ALLE Routen, nicht nur eine einzelne.
 */
function RootComponent() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const { i18n } = useTranslation()

  useEffect(() => {
    setPageMeta(pathname)
  }, [pathname, i18n.language])

  // Reichweitenmessung: meldet Seitenaufrufe nur, wenn zugestimmt wurde und
  // gtag deshalb ueberhaupt geladen ist. Eine SPA loest von sich aus nur beim
  // ersten Laden ein page_view aus — ohne diesen Effekt saehe die Statistik
  // jede Sitzung als eine einzige Seite.
  useEffect(() => {
    trackPageView(pathname)
  }, [pathname])

  // Werbung wie in v1: nur für anonyme und nicht zahlende Besucher. Die Regel
  // selbst inklusive Wartezustand steckt in shouldLoadAdsNow (lib/ads.ts).
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const plan = useUserStore((state) => state.user?.plan)

  useEffect(() => {
    if (shouldLoadAdsNow(isAuthenticated, plan)) loadAds()
  }, [isAuthenticated, plan])

  // i18n-Fix: <html lang> folgte nie dem Sprachwechsel (immer statisch "de" aus
  // index.html) — relevant für Screenreader/Bildschirmlesesprache. Zentral hier
  // gesetzt, damit jede Sprachänderung (Settings-Switcher) global greift.
  useEffect(() => {
    document.documentElement.lang = i18n.resolvedLanguage ?? i18n.language
  }, [i18n.language, i18n.resolvedLanguage])

  return (
    <>
      <Outlet />
      <CookieConsent />
    </>
  )
}

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFound,
  // i18n-Fix (i18n100-5): Die Boundary rendert, wenn der Baum bereits abgestürzt ist —
  // i18next selbst kann betroffen sein. Deshalb hier bewusst `i18next.t` (kein
  // `useTranslation`-Hook, kein React-Kontext nötig) MIT hartem zweisprachigem
  // `defaultValue`-Fallback — die einzige zulässige Ausnahme im gesamten i18n100-Epic.
  errorComponent: (props) => (
    <div className="error-fallback" style={{ padding: '24px', textAlign: 'center' }}>
      <h2 style={{ marginBottom: '16px' }}>
        {i18next.t('common:errorBoundary.title', {
          defaultValue: 'Ups! Ein unerwarteter Fehler ist aufgetreten. / Oops! An unexpected error occurred.',
        })}
      </h2>
      <p style={{ color: 'var(--color-danger, red)', marginBottom: '24px' }}>
        {props.error instanceof Error
          ? props.error.message
          : i18next.t('common:errorBoundary.unknownError', {
              defaultValue: 'Unbekannter Fehler / Unknown error',
            })}
      </p>
      <button onClick={() => window.location.reload()} className="btn btn-primary">
        {i18next.t('common:errorBoundary.reload', {
          defaultValue: 'App neu laden / Reload app',
        })}
      </button>
    </div>
  ),
})
